import { useEffect, useState } from 'react';

/**
 * Current user's profile from the ProbeStack admin-backend's
 * `/users/context-token/me` — the same session check
 * forgesphere-api-lifecycle does (src/features/session/*), ported to plain
 * fetch:
 *
 *  - The session lives in the ProbeStack SSO cookie (HttpOnly, so JS can't
 *    read it), so the request is sent with `credentials: 'include'` and
 *    WITHOUT waiting for a token in localStorage. Requiring
 *    localStorage.authToken first is what left the header on "Guest" after a
 *    normal SSO login. A bearer token (from the landing-page URL hand-off)
 *    is only used as a retry when the cookie alone isn't accepted.
 *  - `/me` nests the identity (`data.user`, `data.profile`, …), so fields
 *    are read from every candidate object, not just the top level.
 *  - The resolved identity is cached under the same localStorage keys
 *    api-lifecycle writes (userEmail, userName, userRole, …); a 401/403
 *    clears them so a stale user is never shown.
 *  - One request per page load, shared by every component using the hook.
 *  - Requested same-origin and proxied to probestack.io by nginx (and the
 *    dev server): the admin-backend's CORS allowlist only includes the
 *    lifecycle host, so a direct cross-origin call from this app is blocked
 *    by the browser and the header stayed on "Guest".
 */

const PROFILE_URL = '/admin-backend/api/public/users/context-token/me';
const PROFILE_CACHE_KEY = 'forgesphere.session.profile';
const IDENTITY_KEYS = ['userId', 'userEmail', 'userFirstName', 'userName', 'userRole', 'userOrganization', 'userOrganizationId'];

const isRecord = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);

// Mirrors api-lifecycle's findProfileDetails: merge every place /me may put
// the identity so a field is found wherever the backend nests it.
function findProfileDetails(payload) {
  const data = isRecord(payload?.data) ? payload.data : null;
  const candidates = [payload, data, payload?.profile, payload?.user, data?.profile, data?.user].filter(isRecord);
  return Object.assign({}, ...candidates);
}

function readText(obj, keys) {
  for (const key of keys) {
    const value = obj?.[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number') return String(value);
  }
  return '';
}

function readRole(details, payload) {
  const direct = readText(details, ['roleName', 'role_name', 'role', 'userRole']);
  if (direct) return direct;
  const data = isRecord(payload?.data) ? payload.data : payload;
  const roles = Array.isArray(details.roles) ? details.roles : Array.isArray(data?.business_roles) ? data.business_roles : [];
  const names = roles
    .map((r) => (typeof r === 'string' ? r : isRecord(r) ? readText(r, ['name', 'role', 'authority', 'key']) : ''))
    .filter(Boolean);
  if (names.length) return [...new Set(names)].join(', ');
  const admin = data?.administration;
  if (admin?.is_super_admin) return 'Super Admin';
  if (admin?.is_org_admin) return 'Org Admin';
  if (admin?.is_admin) return 'Admin';
  return '';
}

function readOrganization(details) {
  const direct = readText(details, ['organizationName', 'organization_name', 'companyName', 'company_name', 'tenantName', 'tenant_name']);
  if (direct) return direct;
  if (isRecord(details.organization)) return readText(details.organization, ['name', 'displayName']);
  return readText(details, ['organization']);
}

function toProfile(payload) {
  const details = findProfileDetails(payload);
  const firstName = readText(details, ['firstName', 'first_name', 'givenName', 'given_name']);
  const lastName = readText(details, ['lastName', 'last_name', 'familyName', 'family_name']);
  const email = readText(details, ['email', 'userEmail', 'user_email']);
  const name = readText(details, ['displayName', 'display_name', 'fullName', 'full_name', 'name', 'username', 'preferredUsername', 'preferred_username'])
    || readText(details.admin, ['name'])
    || [firstName, lastName].filter(Boolean).join(' ')
    || email.split('@')[0];
  const organizationRecord = isRecord(details.organization) ? details.organization : {};
  const accountType = readText(details, ['user_type', 'userType']) || readText(organizationRecord, ['account_type']) || readText(details, ['type']);
  return {
    id: readText(details, ['id', 'userId', 'user_id', 'sub', 'subject']),
    name,
    firstName: firstName || name,
    email,
    role: readRole(details, payload) || 'User',
    organization: readOrganization(details),
    organizationId: readText(details, ['organizationId', 'organization_id', 'companyId', 'company_id', 'tenantId', 'tenant_id']) || readText(organizationRecord, ['id', 'key']),
    userType: /org|enterprise/i.test(accountType) ? 'organization' : 'individual',
  };
}

function writeStorage(key, value) {
  try {
    if (value) localStorage.setItem(key, value);
  } catch {
    // Storage restrictions must not break the app shell.
  }
}

function saveIdentity(payload, p) {
  writeStorage(PROFILE_CACHE_KEY, JSON.stringify(payload));
  writeStorage('userId', p.id);
  writeStorage('userEmail', p.email);
  writeStorage('userFirstName', p.firstName);
  writeStorage('userName', p.name);
  writeStorage('userRole', p.role);
  writeStorage('userOrganization', p.organization);
  writeStorage('userOrganizationId', p.organizationId);
}

function clearIdentity() {
  try {
    [PROFILE_CACHE_KEY, ...IDENTITY_KEYS].forEach((k) => localStorage.removeItem(k));
  } catch {
    // ignore
  }
}

function readStoredToken() {
  try {
    return localStorage.getItem('authToken') || '';
  } catch {
    return '';
  }
}

export class SessionError extends Error {
  constructor(status) {
    super(`Profile request failed (${status})`);
    this.status = status;
  }
}

async function requestProfile(headers) {
  const res = await fetch(PROFILE_URL, { credentials: 'include', headers: { Accept: 'application/json', ...headers } });
  if (!res.ok) throw new SessionError(res.status);
  return res.json();
}

// Cookie first (the real SSO session, like api-lifecycle); bearer token
// only as a fallback for sessions handed over via URL params, or when the
// cookie request didn't get through at all (network/proxy failure).
async function fetchProfile() {
  try {
    return await requestProfile({});
  } catch (err) {
    const token = readStoredToken();
    const rejected = err instanceof SessionError && (err.status === 401 || err.status === 403);
    if (token && (rejected || !(err instanceof SessionError))) {
      return requestProfile({ Authorization: `Bearer ${token}` });
    }
    throw err;
  }
}

// Shared across hook instances (header + Profile page) — one /me per load.
// Also the app's auth gate (App.jsx): /me is the only reliable way to know
// whether the HttpOnly SSO cookie holds a valid session.
let profilePromise = null;
export function loadProfileOnce() {
  if (!profilePromise) {
    profilePromise = fetchProfile()
      .then((payload) => {
        const p = toProfile(payload);
        saveIdentity(payload, p);
        return p;
      })
      .catch((err) => {
        if (err instanceof SessionError && (err.status === 401 || err.status === 403)) clearIdentity();
        profilePromise = null; // allow a later retry
        throw err;
      });
  }
  return profilePromise;
}

function cachedProfile() {
  try {
    const raw = localStorage.getItem(PROFILE_CACHE_KEY);
    return raw ? toProfile(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

function fallbackProfile(error) {
  const email = localStorage.getItem('userEmail') || '';
  return {
    name: localStorage.getItem('userName') || localStorage.getItem('userFirstName') || email.split('@')[0] || 'Guest',
    email: email || 'Not logged in',
    organization: localStorage.getItem('userOrganization') || '',
    role: localStorage.getItem('userRole') || '',
    userType: 'individual',
    isLoading: false,
    error,
  };
}

export function useProbeStackProfile() {
  // Start from the last cached /me result so the header shows the real user
  // immediately instead of flashing "Guest" while the request is in flight.
  const [profile, setProfile] = useState(() => {
    const cached = cachedProfile();
    return cached
      ? { ...cached, isLoading: true, error: null }
      : { name: '', email: '', organization: '', role: '', userType: 'individual', isLoading: true, error: null };
  });

  useEffect(() => {
    let cancelled = false;
    loadProfileOnce()
      .then((p) => {
        if (!cancelled) setProfile({ ...p, isLoading: false, error: null });
      })
      .catch((err) => {
        if (cancelled) return;
        const expired = err instanceof SessionError && (err.status === 401 || err.status === 403);
        setProfile(fallbackProfile(expired ? 'No session' : 'Network error'));
      });
    return () => { cancelled = true; };
  }, []);

  return profile;
}
